import { FC, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { getPresignedUploadUrl, uploadFileToS3, confirmUpload } from 'src/services/survey/api'
import { useSurveyForm } from 'src/hooks/survey/survey-provider'

type UploadState = 'idle' | 'uploading' | 'success' | 'error'

type FileUploadFieldProps = {
  /** The S3 object key (stored value) or null if no file uploaded */
  value: string | null
  /** Whether the field is disabled */
  disabled?: boolean
  /** Callback to update the response value (S3 object key) */
  handleResponse: (value: string | null) => void
  /** Validators from the question's validatorsRef */
  validatorsRef?: Array<{ validationType: string; value: Record<string, unknown> }>
  /** Question ID - required for getting presigned URLs */
  questionId: string
}

/**
 * Extract filename from S3 object key.
 * Object keys are formatted as: {location}/surveys/{survey_id}/questions/{question_id}/{uuid}_{filename}
 */
const getFilenameFromObjectKey = (objectKey: string): string => {
  const parts = objectKey.split('/')
  const lastPart = parts[parts.length - 1]
  // Remove the UUID prefix (8 chars + underscore)
  const underscoreIndex = lastPart.indexOf('_')
  if (underscoreIndex > 0) {
    return lastPart.substring(underscoreIndex + 1)
  }
  return lastPart
}

/**
 * File upload field for survey questions.
 * Uploads files directly to S3 using presigned URLs and stores the object key as the response.
 */
const FileUploadField: FC<FileUploadFieldProps> = ({
  value,
  disabled,
  handleResponse,
  validatorsRef,
  questionId,
}) => {
  const { t } = useTranslation()
  const maxSizeValidator = validatorsRef?.find((v) => v.validationType === 'MAX_FILE_SIZE')
  const fileTypesValidator = validatorsRef?.find((v) => v.validationType === 'ALLOWED_FILE_TYPES')
  const maxSizeMB = (maxSizeValidator?.value?.max_mb as number) ?? 10
  const allowedTypes = (fileTypesValidator?.value?.types as string[]) ?? []

  // tn_surveys binds every upload key to the instance it was issued for.
  const { instanceId, setIsUploading } = useSurveyForm()
  const [uploadState, setUploadState] = useState<UploadState>('idle')

  // Block survey navigation until the upload settles (and on unmount mid-upload).
  useEffect(() => {
    setIsUploading(uploadState === 'uploading')
    return () => setIsUploading(false)
  }, [uploadState, setIsUploading])
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  // Local preview of the picked file. The guest flow never asks the server for a
  // download URL: the guest already has the file, and staff read it via the review
  // override.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const previewRef = useRef<string | null>(null)

  const replacePreview = (url: string | null) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current)
    previewRef.current = url
    setPreviewUrl(url)
  }
  useEffect(() => () => replacePreview(null), [])

  const validateFile = (file: File): string | null => {
    // Check file size
    const fileSizeMB = file.size / (1024 * 1024)
    if (fileSizeMB > maxSizeMB) {
      return t('fileUpload.errors.tooLarge', { size: fileSizeMB.toFixed(1), max: maxSizeMB })
    }

    // Check file type if restrictions are set
    if (allowedTypes.length > 0) {
      const isAllowed = allowedTypes.some((allowed) => {
        if (allowed === file.type) return true
        if (allowed.endsWith('/*')) {
          const prefix = allowed.slice(0, -1) // 'image/*' -> 'image/'
          return file.type.startsWith(prefix)
        }
        return false
      })
      if (!isAllowed) {
        return t('fileUpload.errors.wrongType')
      }
    }

    return null
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !instanceId) return

    // Validate file
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      setUploadState('error')
      return
    }

    setError(null)
    setUploadState('uploading')
    setUploadProgress(0)

    try {
      // Get presigned upload URL
      const { uploadUrl, objectKey } = await getPresignedUploadUrl({
        filename: file.name,
        contentType: file.type,
        questionId,
        instance: instanceId,
      })

      // Upload to S3
      await uploadFileToS3(uploadUrl, file, (progress) => {
        setUploadProgress(progress)
      })

      // Confirm upload with backend validation
      const confirmation = await confirmUpload({
        objectKey,
        questionId,
        instance: instanceId,
      })

      if (!confirmation.valid) {
        // Backend validation failed (file already deleted by backend)
        setUploadState('error')
        setError(t('fileUpload.errors.validationFailed'))
        return
      }

      // Success - the answer is saved only now, after the server accepted the file
      replacePreview(URL.createObjectURL(file))
      setUploadState('success')
      handleResponse(objectKey)
    } catch {
      setUploadState('error')
      setError(t('fileUpload.errors.uploadFailed'))
    }
  }

  const handleClear = () => {
    handleResponse(null)
    setUploadState('idle')
    setUploadProgress(0)
    setError(null)
    replacePreview(null)
  }

  const displayFilename = value ? getFilenameFromObjectKey(value) : null

  return (
    <div className="mx-auto w-full max-w-md">
      {value ? (
        // File uploaded state
        <div className="rounded-lg border bg-surface p-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <svg
                className="h-5 w-5 flex-shrink-0 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {previewUrl && (
                <img
                  src={previewUrl}
                  alt={t('fileUpload.previewAlt')}
                  className="h-16 w-16 flex-shrink-0 rounded object-cover"
                  data-testid="file-upload-preview"
                />
              )}
              <span className="truncate text-text-primary">{displayFilename}</span>
            </div>
            <Button
              variant="ghost"
              tone="error"
              size="sm"
              iconOnly
              tooltip={t('fileUpload.removeFile')}
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className="ml-2 flex-shrink-0"
              data-testid="file-upload-remove"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : uploadState === 'uploading' ? (
        // Uploading state
        <div className="rounded-lg border bg-surface p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('common.uploading')}</span>
            <span className="text-sm font-medium text-text-primary">{uploadProgress}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      ) : (
        // Upload prompt state
        <div>
          <label
            className={cn(
              'flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed',
              'border-border bg-surface hover:bg-surface-hover',
              disabled && 'cursor-not-allowed opacity-50',
              uploadState === 'error' && 'border-error',
            )}
          >
            <div className="flex flex-col items-center justify-center pb-6 pt-5">
              <svg
                className={cn(
                  'mb-2 h-8 w-8',
                  uploadState === 'error' ? 'text-error' : 'text-text-secondary',
                )}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-sm text-text-secondary">
                <span className="font-medium">{t('fileUpload.clickToUpload')}</span>{' '}
                {t('fileUpload.orDragAndDrop')}
              </p>
              {allowedTypes.length > 0 && (
                <p className="mt-1 text-xs text-text-secondary">{allowedTypes.join(', ')}</p>
              )}
              <p className="text-xs text-text-secondary">
                {t('fileUpload.maxSize')} {maxSizeMB}MB
              </p>
            </div>
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={disabled}
              accept={allowedTypes.length > 0 ? allowedTypes.join(',') : undefined}
              data-testid="file-upload-input"
            />
          </label>
          {error && <p className="mt-2 text-sm text-error">{error}</p>}
        </div>
      )}
    </div>
  )
}

export default FileUploadField
